<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-justify">
        <h2 class="display-2 text-center mb-4 text-secondary">Changelog & Roadmap</h2>

        <p class="text-body-1">
          Welcome to the Changelog & Roadmap page for the Holonet Galactic Encyclopedia! Here, you can stay up to date
          with the latest updates and changes to the project, as well as get a glimpse of our exciting plans for the
          future.
        </p>

        <h3 class="mt-5 text-secondary">The Vision and Roadmap</h3>
        <p class="text-body-1">
          The ultimate vision behind this project is to provide an immersive, interactive, and comprehensive way to
          explore the Star Wars universe. This initiative aspires to become the definitive tool for fans, researchers,
          and enthusiasts to navigate the galaxy with unprecedented depth and ease. Here's the roadmap to achieving this
          vision:
        </p>
        <h4 class="mt-2">2D Graphical Atlas</h4>
        <p class="text-body-1">
          Develop an intuitive graphical atlas allowing to visually navigate the galaxy, explore key regions, systems,
          planets, and points of interest.
        </p>
        <h4 class="mt-2">Expanded Data and Characteristics</h4>
        <ul class="text-body-1 pl-6">
          <li>Enrich the database with detailed planetary characteristics, including diameter, population, and other
            vital statistics.</li>
          <li>Add connections to Star Wars media (movies, series, games, books, comics) to show where each planet,
            system, or event appears.</li>
          <li>Introduce detailed data on galactic systems, regions, and their political or historical significance.</li>
          <li>Expanding the database with more categories like vehicles, species, galactic organizations, ...</li>
        </ul>
        <h4 class="mt-2">Planet Comparators</h4>
        <p class="text-body-1">
          Build powerful comparison tools to analyze planets based on characteristics like size, population, orbital
          period, and more.
        </p>
        <h4 class="mt-2">Advanced Linked Data Integration</h4>
        <p class="text-body-1">
          Expand the interconnectivity between Wookieepedia, Wikidata, and other linked data sources to provide richer,
          more accurate, and contextualized data.
        </p>
        <h4 class="mt-2">Export and Data Accessibility</h4>
        <p class="text-body-1">
          Enable users to export all data in multiple formats such as JSON, RDF/XML, and TTL for further analysis or
          integration with other projects. With your support and the limitless possibilities of linked data, the Holonet
          Galactic Encyclopedia will transform how Star Wars fans explore their favorite galaxy. Whether you're diving
          into the specifics of Coruscant's population or comparing the diameters of Tatooine and Naboo, this project
          aims to be a one-stop galactic resource. Together, we will make the galaxy closer than ever before.
        </p>
        
         <h3 class="mt-5 text-secondary">Version 1.2.0 - TBD (In progress)</h3>
        <h4>New features</h4>
        <ul class="pl-6">
          <li>Interactive galaxy map with zoom and pan + sidebar with advanced planet search and filtering.</li>
          <li>Updated characteristics for planets and their occurences in media.</li>
          <li>Beta version of the graphical visualization of planets and their appearances in films/series/video games.</li>
        </ul>
        <h4>Minor and fixes</h4>
        <ul class="pl-6">
          <li>Performance optimizations for the interactive map.</li>
        </ul>

        <h3 class="mt-5 text-secondary">Version 1.1.0 - More Data About Planets and Introduction of Graph viewer, Atlas and HoloExplorer (May 2025)</h3>
        <h4>New features</h4>
        <ul class="pl-6">
          <li>Updated characteristics for over 700 planets, including orbital period, rotation period, and population data.</li>
          <li>Beta version of the 2D graphical atlas featuring a grid-based system.</li>
          <li>Beta version of HoloExplorer: a "Cemantle-like" game set in the SW universe.</li>
          <li>Brand new graphical theme with a subtle SW flavor.</li>
          <li>Pixel art graphical resources for planets and their biomes, created using the Pixel-Planet Generator by Deep-Fold (https://deep-fold.itch.io/pixel-planet-generator).</li>
        </ul>
        <h4>Minor and fixes</h4>
        <ul class="pl-6">
          <li>Added keydown functionality to submit guesses in HoloExplorer for improved user experience.</li>
          <li>Improved UI responsiveness: Fixed layout issues to ensure smoother user interaction across devices.</li>
          <li>Improved color scheme and history of attempts in Holoword.</li>
          <li>Enhanced resolution of GraphViewer to better support widescreen.</li>
          <li>Better alignment of axis in the 2D atlas.</li>
        </ul>

        <h3 class="mt-5 text-secondary">Version 1.0.0 - Initial Release (January 2025)</h3>
        <h4>New features</h4>
        <ul class="pl-6">
          <li>Basic Data Import: Initial data import of planets and some systems from Wikidata. Import completed for a set of core planets, with more data being added continuously.</li>
          <li>Basic User Interface: Created the first version of the user interface for the site, allowing users to browse basic planetary data. UI design is functional, but it’s in its early stages and will receive future updates.</li>
          <li>Search Functionality: Implemented a search function to filter and sort planets based on various attributes.</li>
        </ul>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { useHead } from '@unhead/vue';
  
export default {
  name: 'About',
  setup() {
    useHead({
      title: "Holonet Changelog & Roadmap – Latest Updates & Future Features",
      meta: [
        {
          name: "description",
          content: "Follow the latest updates, bug fixes, and new features of the Holonet Galactic Encyclopedia. Discover our roadmap for upcoming tools, data expansions, and interactive enhancements for the ultimate Star Wars knowledge base.",
        },
        {
          property: "og:title",
          content: "Holonet Changelog & Roadmap – Latest Updates & Future Features",
        },
        {
          property: "og:description",
          content: "Stay informed about the Holonet Galactic Encyclopedia’s progress. Explore our changelog for recent updates and the roadmap for future features, data additions, and interactive tools.",
        },
        {
          property: "og:url",
          content: "https://sw-holonet.com/roadmap",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "Holonet Changelog & Roadmap – Latest Updates & Future Features",
        },
        {
          name: "twitter:description",
          content: "Track the latest updates and upcoming features of the Holonet Galactic Encyclopedia. Stay ahead with our changelog and roadmap for Star Wars data exploration.",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Holonet changelog, Holonet roadmap, Star Wars updates, Star Wars data, Holonet features, Star Wars knowledge base, interactive tools, Wikidata Star Wars, Holonet progress",
        },
      ],
    });
  }
};
</script>
